/**
 * The DataSphere capability map: every self-service action or page the
 * Platform Roadmap 2026 (#287504) and 2027 (#292471) would put in the portal,
 * grouped by what the user wants to do rather than by roadmap Feature.
 *
 * Live entries point at a scaffolder template or a page. Planned entries carry
 * the roadmap year and the ADO story ids they come from, so the Services page
 * and the home page can show what is coming without a dead link.
 *
 * Backend-only stories (Foundation, Automation Framework internals, IaC
 * engines) have no surface and are not listed.
 */

export type Availability = 'live' | 'roadmap-2026' | 'roadmap-2027';

export type CapabilityArea = {
  id: string;
  title: string;
  description: string;
};

export type DataSphereCapability = {
  id: string;
  area: string;
  title: string;
  description: string;
  availability: Availability;
  /** ADO user story ids in GDL-Hub spoke. */
  roadmapIds: number[];
  /** Scaffolder template name, for live services. */
  templateName?: string;
  /** Portal path, for live pages. */
  href?: string;
  /** True for pages and dashboards, false for request forms. */
  isPage?: boolean;
};

/** Display order is the order of this list. */
export const CAPABILITY_AREAS: CapabilityArea[] = [
  {
    id: 'spokes',
    title: 'Spokes and Workspaces',
    description:
      'Get a new spoke for your team and keep it set up the way you need it.',
  },
  {
    id: 'data',
    title: 'Data, Catalogs and Sharing',
    description:
      'Create catalogs and schemas, connect to sources and share data safely.',
  },
  {
    id: 'access',
    title: 'Access, Identity and Secrets',
    description:
      'Decide who can use your spoke and give your automation what it needs.',
  },
  {
    id: 'ai',
    title: 'Data Science, ML and AI',
    description:
      'Build, serve and govern models, agents and AI applications on governed data.',
  },
  {
    id: 'golden-paths',
    title: 'Golden Paths',
    description:
      'Start from an approved pattern so your design does not need its own ARB review.',
  },
  {
    id: 'insights',
    title: 'Observability Centre',
    description:
      'One place to see how your spoke and its services are running, what they cost and how good the data is.',
  },
];

export const CAPABILITIES: DataSphereCapability[] = [
  // Spokes and Workspaces
  {
    id: 'spoke-request',
    area: 'spokes',
    title: 'Request a new spoke',
    description:
      'A new DataSphere spoke with its workspaces, storage and persona groups.',
    availability: 'live',
    roadmapIds: [287508, 289623],
    templateName: 'howden-databricks-spoke-request',
  },
  {
    id: 'spoke-baseline',
    area: 'spokes',
    title: 'Choose spoke baseline options',
    description:
      'Pick compute policies, SQL warehouse sizes and budgets when the spoke is built, and change them later.',
    availability: 'roadmap-2026',
    roadmapIds: [292616, 288164, 292563, 292564, 292565],
  },
  {
    id: 'spoke-add-ons',
    area: 'spokes',
    title: 'Add Data Factory or Power BI to a spoke',
    description: 'Optional products added to an existing spoke.',
    availability: 'roadmap-2026',
    roadmapIds: [287509, 290848],
  },
  {
    id: 'spoke-manage',
    area: 'spokes',
    title: 'Manage compute, SQL warehouses and budgets',
    description:
      'Resize SQL warehouses, change compute and set spend alerts on a spoke you own.',
    availability: 'roadmap-2027',
    roadmapIds: [287526, 292567, 292566, 292641],
  },
  {
    id: 'workspace-lifecycle',
    area: 'spokes',
    title: 'Create, destroy or borrow a workspace',
    description:
      'Add or remove workspaces, or get a temporary workspace that is removed for you.',
    availability: 'roadmap-2027',
    roadmapIds: [287527, 289624],
  },

  // Data, Catalogs and Sharing
  {
    id: 'catalog-request',
    area: 'data',
    title: 'Request catalogs and schemas',
    description:
      'New Unity Catalog catalogs and schemas on an existing workspace, named and tagged for you.',
    availability: 'live',
    roadmapIds: [288156, 289684, 290412, 293488, 295152],
    templateName: 'howden-databricks-catalog-schema-request',
  },
  {
    id: 'catalog-manage',
    area: 'data',
    title: 'Manage catalogs and schemas',
    description: 'Change or retire catalogs and schemas you already own.',
    availability: 'roadmap-2027',
    roadmapIds: [287519],
  },
  {
    id: 'external-location',
    area: 'data',
    title: 'Request an external location',
    description: 'Register governed cloud storage with Unity Catalog.',
    availability: 'roadmap-2027',
    roadmapIds: [292571],
  },
  {
    id: 'federation',
    area: 'data',
    title: 'Request a Lakehouse Federation connection',
    description:
      'Query an external database from Databricks without copying it.',
    availability: 'roadmap-2027',
    roadmapIds: [292573],
  },
  {
    id: 'ingestion-source',
    area: 'data',
    title: 'Open the firewall to a source system',
    description:
      'Let your spoke reach an external source. The firewall lists update automatically once approved.',
    availability: 'roadmap-2026',
    roadmapIds: [291160, 289613],
  },
  {
    id: 'delta-share',
    area: 'data',
    title: 'Share data with another spoke or region',
    description: 'Request and manage Delta Shares between spokes and regions.',
    availability: 'roadmap-2026',
    roadmapIds: [292614, 287522],
  },
  {
    id: 'marketplace',
    area: 'data',
    title: 'Publish a Marketplace listing or a Clean Room',
    description:
      'Offer a data product in the private Marketplace or collaborate in a Clean Room.',
    availability: 'roadmap-2027',
    roadmapIds: [292588, 292589],
  },

  // Access, Identity and Secrets
  {
    id: 'group-membership',
    area: 'access',
    title: 'Manage group membership',
    description:
      'Add or remove people from the persona groups of a spoke you administer.',
    availability: 'live',
    roadmapIds: [287513],
    templateName: 'howden-user-groups-request',
  },
  {
    id: 'key-vault',
    area: 'access',
    title: 'Manage Key Vault secrets and secret scopes',
    description:
      'A Key Vault backed secret scope for your spoke, managed from the portal.',
    availability: 'roadmap-2026',
    roadmapIds: [287515, 292572],
  },
  {
    id: 'service-principal',
    area: 'access',
    title: 'Request a service principal for automation',
    description:
      'An identity for your pipelines, with workload identity federation and no secrets to rotate.',
    availability: 'roadmap-2027',
    roadmapIds: [292570],
  },
  {
    id: 'library-allowlist',
    area: 'access',
    title: 'Request a library or init script',
    description: 'Ask for a library to be added to the approved allowlist.',
    availability: 'roadmap-2027',
    roadmapIds: [292576],
  },
  {
    id: 'unmasking',
    area: 'access',
    title: 'Request an unmasking exemption',
    description:
      'Time-limited access to masked columns, approved by the data owner.',
    availability: 'roadmap-2027',
    roadmapIds: [292580, 292578],
  },

  // Data Science, ML and AI
  {
    id: 'ml-workspace',
    area: 'ai',
    title: 'Add an ML workspace to a spoke',
    description:
      'A workspace set up for data science, with GPU quota on request.',
    availability: 'roadmap-2027',
    roadmapIds: [292642, 292647],
  },
  {
    id: 'mlops-template',
    area: 'ai',
    title: 'Start a data science project',
    description:
      'A repository and MLOps pipeline from the template, with feature engineering in Unity Catalog.',
    availability: 'roadmap-2027',
    roadmapIds: [292643, 292644],
  },
  {
    id: 'model-serving',
    area: 'ai',
    title: 'Serve a model',
    description:
      'Model serving through the AI Gateway, published as a documented API and monitored.',
    availability: 'roadmap-2027',
    roadmapIds: [292645, 292654, 292646],
  },
  {
    id: 'foundation-models',
    area: 'ai',
    title: 'Use foundation models and Vector Search',
    description:
      'Governed access to foundation models and a Vector Search baseline for RAG.',
    availability: 'roadmap-2027',
    roadmapIds: [292648, 292583],
  },
  {
    id: 'share-models',
    area: 'ai',
    title: 'Share models and features',
    description: 'Reuse models and features across spokes and divisions.',
    availability: 'roadmap-2027',
    roadmapIds: [292653],
  },
  {
    id: 'publish-business',
    area: 'ai',
    title: 'Publish to business users',
    description:
      'Release a Databricks App, an AI/BI dashboard or a Genie space over certified Gold data.',
    availability: 'roadmap-2027',
    roadmapIds: [292651, 292652, 292650],
  },
  {
    id: 'ds-showcase',
    area: 'ai',
    title: 'Data science showcase',
    description: 'See what other teams have built and reuse it.',
    availability: 'roadmap-2027',
    roadmapIds: [292655],
    isPage: true,
  },

  // Golden Paths
  {
    id: 'pattern-catalogue',
    area: 'golden-paths',
    title: 'Browse approved patterns',
    description:
      'The ARB approved pattern catalogue with the Terraform modules behind each pattern.',
    availability: 'roadmap-2027',
    roadmapIds: [292481, 292482],
    isPage: true,
  },
  {
    id: 'pattern-app',
    area: 'golden-paths',
    title: 'Pattern: Databricks App',
    description: 'Build and host an internal app on governed data.',
    availability: 'roadmap-2027',
    roadmapIds: [292484],
  },
  {
    id: 'pattern-pipelines',
    area: 'golden-paths',
    title: 'Pattern: ingestion, batch and streaming',
    description:
      'Bring in an external source and run batch or streaming pipelines the approved way.',
    availability: 'roadmap-2027',
    roadmapIds: [292486, 292485],
  },
  {
    id: 'pattern-serving',
    area: 'golden-paths',
    title: 'Pattern: serving and consumption',
    description: 'Expose data to reports, APIs and other teams.',
    availability: 'roadmap-2027',
    roadmapIds: [292487],
  },
  {
    id: 'pattern-ai',
    area: 'golden-paths',
    title: 'Pattern: ML model, agent or RAG app',
    description:
      'Approved starting points for machine learning and AI applications.',
    availability: 'roadmap-2027',
    roadmapIds: [292488, 292649],
  },
  {
    id: 'ai-architect',
    area: 'golden-paths',
    title: 'Ask the AI solution architect',
    description: 'Describe what you need and get the pattern that fits.',
    availability: 'roadmap-2027',
    roadmapIds: [293847],
  },
  {
    id: 'pattern-contribute',
    area: 'golden-paths',
    title: 'Contribute a pattern',
    description: 'Propose a new pattern for review and publication.',
    availability: 'roadmap-2027',
    roadmapIds: [292490],
  },

  // Dashboards: one Observability Centre, one view per subject.
  {
    id: 'hubs-spokes',
    area: 'insights',
    title: 'Hubs and spokes',
    description: 'Every hub and spoke, its region, owner and status.',
    availability: 'live',
    roadmapIds: [],
    href: '/spoke-overview',
    isPage: true,
  },
  {
    id: 'obs-health',
    area: 'insights',
    title: 'Spoke health and integration services',
    description:
      'Jobs, compute, SQL warehouses, model serving, Data Factory and Logic Apps for your spoke.',
    availability: 'roadmap-2027',
    roadmapIds: [292584, 292585, 292646],
    href: '/observability?view=health',
    isPage: true,
  },
  {
    id: 'obs-cost',
    area: 'insights',
    title: 'Cost and chargeback',
    description: 'Spend by service, budget against actual and savings.',
    availability: 'roadmap-2026',
    roadmapIds: [287524, 289634, 292566],
    href: '/observability?view=cost',
    isPage: true,
  },
  {
    id: 'obs-estate',
    area: 'insights',
    title: 'Estate, tags and ownership',
    description: 'What exists, who owns it and whether it is tagged correctly.',
    availability: 'roadmap-2027',
    roadmapIds: [289660, 289654, 287525],
    href: '/observability?view=estate',
    isPage: true,
  },
  {
    id: 'obs-quality',
    area: 'insights',
    title: 'Data quality, certification and lineage',
    description: 'Quality scores, certified data products and lineage.',
    availability: 'roadmap-2027',
    roadmapIds: [292582, 289631, 292579],
    href: '/observability?view=quality',
    isPage: true,
  },
];

export const AVAILABILITY_LABEL: Record<Availability, string> = {
  live: 'Available now',
  'roadmap-2026': 'Coming in 2026',
  'roadmap-2027': 'Planned for 2027',
};

export const capabilitiesInArea = (areaId: string) =>
  CAPABILITIES.filter(capability => capability.area === areaId);

export const capabilityForTemplate = (templateName: string) =>
  CAPABILITIES.find(capability => capability.templateName === templateName);

/** Services sub-page root of the scaffolder. */
export const SERVICES_ROOT = '/create/templates';

/** Where a service opens: its form when live, its placeholder when planned. */
export const serviceHref = (capability: DataSphereCapability) =>
  capability.availability === 'live' && capability.templateName
    ? `${SERVICES_ROOT}/default/${capability.templateName}`
    : `${SERVICES_ROOT}/planned/${capability.id}`;
